import type { LucideIcon } from 'lucide-react';
import {
  LayoutGrid,
  MessageSquare,
  FileText,
  Library,
  Sparkles,
  Users,
  Cpu,
  Wind,
  Box,
  Mountain,
  Table2,
  Workflow,
  CircuitBoard,
  CalendarDays,
  BrickWall,
  Droplets,
  MapPinned,
  Zap,
  Thermometer,
  PenLine,
} from 'lucide-react';

export type TileVisibility =
  | 'all'
  | 'system_admin'
  | 'user_admin'
  | 'company_required'
  | 'platform_admin';

/** Static catalog sections (recents are driven by server preferences). */
export type CatalogSection = 'favorites' | 'coming_soon' | 'admin';

/** Engineering domain for grouping under “Coming soon”. */
export type ComingSoonDomainId =
  | 'process_fluid'
  | 'mechanical_structural'
  | 'electronics_power'
  | 'thermal_energy'
  | 'water_earth'
  | 'geospatial'
  | 'project_delivery'
  | 'documentation';

export const COMING_SOON_DOMAIN_LABELS: Record<ComingSoonDomainId, string> = {
  process_fluid: 'Process & fluid systems',
  mechanical_structural: 'Mechanical & structural',
  electronics_power: 'Electronics & power',
  thermal_energy: 'Thermal & energy',
  water_earth: 'Water & earth resources',
  geospatial: 'Geospatial',
  project_delivery: 'Project delivery',
  documentation: 'Documentation & drafting',
};

export const COMING_SOON_DOMAIN_ORDER: ComingSoonDomainId[] = [
  'process_fluid',
  'mechanical_structural',
  'electronics_power',
  'thermal_energy',
  'water_earth',
  'geospatial',
  'project_delivery',
  'documentation',
];

export interface AppTileDefinition {
  id: string;
  title: string;
  description: string;
  to: string;
  icon: LucideIcon;
  visibility: TileVisibility;
  /** Sections where this tile appears (same app may appear in more than one). */
  catalogSections: CatalogSection[];
  /** Shown on tile when not yet implemented */
  comingSoon?: boolean;
  /** Required when comingSoon — used for subsection under Coming soon */
  comingSoonDomain?: ComingSoonDomainId;
  /** Same-origin tile opens in a new tab (active app surfaces default to this). */
  openInNewTab?: boolean;
}

export const APP_TILES: AppTileDefinition[] = [
  {
    id: 'workspace',
    title: 'Workspace',
    description: 'Projects, chat, and your engineering hub',
    to: '/dashboard',
    icon: LayoutGrid,
    visibility: 'all',
    catalogSections: ['favorites'],
    openInNewTab: true,
  },
  {
    id: 'chat',
    title: 'Chat',
    description: 'Agent conversations and session history',
    to: '/chat',
    icon: MessageSquare,
    visibility: 'all',
    catalogSections: ['favorites'],
    openInNewTab: true,
  },
  {
    id: 'company-documents',
    title: 'Company Documents',
    description: 'Tenant-wide documents and templates',
    to: '/company-documents',
    icon: Library,
    visibility: 'all',
    catalogSections: ['favorites'],
    openInNewTab: true,
  },
  {
    id: 'documents',
    title: 'Docs Editor',
    description: 'Rich document drafting with agent assist',
    to: '/documents/new',
    icon: FileText,
    visibility: 'all',
    catalogSections: ['favorites'],
    openInNewTab: true,
  },
  {
    id: 'standards',
    title: 'Standards Studio',
    description: 'Upload and index proprietary standards',
    to: '/standards-studio',
    icon: FileText,
    visibility: 'system_admin',
    catalogSections: ['favorites'],
    openInNewTab: true,
  },
  {
    id: 'deliverables',
    title: 'Deliverables Studio',
    description: 'Deliverables and review workflows',
    to: '/deliverables',
    icon: FileText,
    visibility: 'system_admin',
    catalogSections: ['favorites'],
    openInNewTab: true,
  },
  {
    id: 'customize-agents',
    title: 'Customize Agents',
    description: 'Fine-tuning and agent configuration',
    to: '/fine-tune',
    icon: Sparkles,
    visibility: 'company_required',
    catalogSections: ['favorites'],
    openInNewTab: true,
  },
  {
    id: 'spreadsheets',
    title: 'Spreadsheets',
    description: 'Workbook editing with agent assist',
    to: '/spreadsheets/new',
    icon: Table2,
    visibility: 'all',
    catalogSections: ['favorites'],
    openInNewTab: true,
  },
  {
    id: 'user-admin',
    title: 'User admin',
    description: 'Manage users and access',
    to: '/user-admin',
    icon: Users,
    visibility: 'user_admin',
    catalogSections: ['admin'],
    openInNewTab: true,
  },
  {
    id: 'pid',
    title: 'P&ID',
    description: 'Piping & instrumentation diagrams',
    to: '/pid',
    icon: Workflow,
    visibility: 'all',
    catalogSections: ['favorites'],
    openInNewTab: true,
  },
  {
    id: 'sld',
    title: 'SLD',
    description: 'Single line diagram',
    to: '/single-line',
    icon: Workflow,
    visibility: 'all',
    catalogSections: ['favorites'],
    openInNewTab: true,
  },
  {
    id: 'cad',
    title: 'CAD',
    description: 'In-browser CAD and collaboration',
    to: '/cad',
    icon: Box,
    visibility: 'all',
    catalogSections: ['favorites'],
    openInNewTab: true,
  },
  {
    id: 'fea',
    title: 'FEA',
    description: 'Finite element analysis',
    to: '/fea',
    icon: Cpu,
    visibility: 'all',
    catalogSections: ['coming_soon'],
    comingSoon: true,
    comingSoonDomain: 'mechanical_structural',
  },
  {
    id: 'structural-analysis',
    title: 'Structural analysis',
    description: 'Structural models and code checks',
    to: '/structural-analysis',
    icon: BrickWall,
    visibility: 'all',
    catalogSections: ['coming_soon'],
    comingSoon: true,
    comingSoonDomain: 'mechanical_structural',
  },
  {
    id: 'cfd',
    title: 'CFD',
    description: 'Computational fluid dynamics',
    to: '/cfd',
    icon: Wind,
    visibility: 'all',
    catalogSections: ['coming_soon'],
    comingSoon: true,
    comingSoonDomain: 'process_fluid',
  },
  {
    id: 'eda',
    title: 'EDA',
    description: 'Electronic design automation',
    to: '/eda',
    icon: CircuitBoard,
    visibility: 'all',
    catalogSections: ['coming_soon'],
    comingSoon: true,
    comingSoonDomain: 'electronics_power',
  },
  {
    id: 'power-systems',
    title: 'Power systems modeling',
    description: 'Electrical power flow and stability',
    to: '/power-systems',
    icon: Zap,
    visibility: 'all',
    catalogSections: ['coming_soon'],
    comingSoon: true,
    comingSoonDomain: 'electronics_power',
  },
  {
    id: 'thermodynamic-modeling',
    title: 'Thermodynamic modeling',
    description: 'Energy balances and process thermodynamics',
    to: '/thermodynamic-modeling',
    icon: Thermometer,
    visibility: 'all',
    catalogSections: ['coming_soon'],
    comingSoon: true,
    comingSoonDomain: 'thermal_energy',
  },
  {
    id: 'water-resources',
    title: 'Water resources',
    description: 'Hydrology, hydraulics, and water systems',
    to: '/water-resources',
    icon: Droplets,
    visibility: 'all',
    catalogSections: ['coming_soon'],
    comingSoon: true,
    comingSoonDomain: 'water_earth',
  },
  {
    id: 'geology',
    title: 'Geology',
    description: 'Geotechnical workflows',
    to: '/geology',
    icon: Mountain,
    visibility: 'all',
    catalogSections: ['coming_soon'],
    comingSoon: true,
    comingSoonDomain: 'water_earth',
  },
  {
    id: 'gis',
    title: 'GIS',
    description: 'Geographic information systems',
    to: '/gis',
    icon: MapPinned,
    visibility: 'all',
    catalogSections: ['coming_soon'],
    comingSoon: true,
    comingSoonDomain: 'geospatial',
  },
  {
    id: 'pm',
    title: 'Project Management',
    description: 'Portfolio tracking, delivery flow, and staged agent planning',
    to: '/pm',
    icon: CalendarDays,
    visibility: 'all',
    catalogSections: ['favorites'],
    openInNewTab: true,
  },
  {
    id: 'drafting',
    title: 'Drafting',
    description: 'Technical drawing and detailing',
    to: '/drafting',
    icon: PenLine,
    visibility: 'all',
    catalogSections: ['coming_soon'],
    comingSoon: true,
    comingSoonDomain: 'documentation',
  },
];

export function resolveTileHref(
  tile: AppTileDefinition,
  companyId: string | undefined
): string {
  if (tile.id === 'customize-agents' && companyId) {
    return `/fine-tune/${companyId}`;
  }
  return tile.to;
}

/** Absolute URL for opening app tiles in a new tab (SPA base path aware). */
export function resolveTileAbsoluteHref(path: string): string {
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  const base = import.meta.env.BASE_URL || '/';
  const baseNorm = base.endsWith('/') ? base.slice(0, -1) : base;
  const pathNorm = path.startsWith('/') ? path : `/${path}`;
  return `${window.location.origin}${baseNorm}${pathNorm}`;
}

export function isAbsoluteHttpUrl(path: string): boolean {
  return path.startsWith('http://') || path.startsWith('https://');
}

/** Same-tab SPA navigation when the tile URL is on the current origin. */
export function isSameOriginTileHref(href: string): boolean {
  if (!isAbsoluteHttpUrl(href)) {
    return true;
  }
  try {
    return new URL(href).origin === window.location.origin;
  } catch {
    return false;
  }
}

/** `pathname` + `search` + `hash` for React Router from an absolute URL. */
export function tileHrefToRouterPath(href: string): string {
  const u = new URL(href);
  return `${u.pathname}${u.search}${u.hash}`;
}

export function isTileVisible(
  tile: AppTileDefinition,
  opts: {
    isSystemAdmin: boolean;
    isUserAdmin: boolean;
    companyId?: string;
    isPlatformAdmin?: boolean;
  }
): boolean {
  if (tile.comingSoon) {
    return true;
  }
  switch (tile.visibility) {
    case 'all':
      return true;
    case 'system_admin':
      return opts.isSystemAdmin;
    case 'user_admin':
      return opts.isUserAdmin;
    case 'company_required':
      return !!opts.companyId;
    case 'platform_admin':
      return !!opts.isPlatformAdmin;
    default:
      return false;
  }
}

export function tilesForCatalogSection(
  visible: AppTileDefinition[],
  section: CatalogSection
): AppTileDefinition[] {
  return visible.filter((t) => t.catalogSections.includes(section));
}

/** Pinnable on the portal (favorites or admin catalog, not coming-soon placeholders). */
export function tileSupportsPortalFavorite(tile: AppTileDefinition): boolean {
  if (tile.comingSoon) {
    return false;
  }
  return (
    tile.catalogSections.includes('favorites') ||
    tile.catalogSections.includes('admin')
  );
}

/** Group visible coming-soon tiles by engineering domain (stable order). */
export function groupComingSoonTilesByDomain(
  tiles: AppTileDefinition[]
): Array<{
  domain: ComingSoonDomainId;
  label: string;
  tiles: AppTileDefinition[];
}> {
  const buckets = new Map<ComingSoonDomainId, AppTileDefinition[]>();
  for (const id of COMING_SOON_DOMAIN_ORDER) {
    buckets.set(id, []);
  }
  for (const t of tiles) {
    if (!t.comingSoon) {
      continue;
    }
    const d = t.comingSoonDomain ?? 'mechanical_structural';
    const list = buckets.get(d);
    if (list) {
      list.push(t);
    } else {
      buckets.get('mechanical_structural')!.push(t);
    }
  }
  for (const list of buckets.values()) {
    list.sort((a, b) => a.title.localeCompare(b.title));
  }
  return COMING_SOON_DOMAIN_ORDER.map((domain) => ({
    domain,
    label: COMING_SOON_DOMAIN_LABELS[domain],
    tiles: buckets.get(domain) ?? [],
  })).filter((g) => g.tiles.length > 0);
}

export type PortalRecentEntry = { tile_id: string; opened_at: string };

export function tilesFromRecents(
  visibleById: Map<string, AppTileDefinition>,
  recents: PortalRecentEntry[]
): AppTileDefinition[] {
  const out: AppTileDefinition[] = [];
  const seen = new Set<string>();
  for (const r of recents) {
    const id = r.tile_id;
    if (!id || seen.has(id)) {
      continue;
    }
    const def = visibleById.get(id);
    if (def && !def.comingSoon) {
      out.push(def);
      seen.add(id);
    }
  }
  return out;
}
